import React, { useState } from 'react';
import { confirmDialog } from '../../lib/confirm';
import { notifyError } from '../../lib/notify';
import { Link } from 'react-router-dom';
import axios from '../../../config/axios';
import { Button, Meta, Display, Chip } from '../../components/Atoms';
import { LoadingState, ErrorState, EmptyState } from '../../components/states';
import { useApi, formatDate } from '../../lib/api';
import CareShell, { CareSection } from './CareShell';

/**
 * CareSprays - lista oprysków (history + active z karencją).
 *
 * Endpointy:
 *   GET /api/sprays/history - chronologiczna lista 100 ostatnich
 *   GET /api/sprays/active - z aktywną karencją (safe_harvest_date >= today)
 *   DELETE /api/sprays/:id
 */

const SPRAY_TYPE_LABEL = {
  fungicide: 'Fungicyd',
  insecticide: 'Insektycyd',
  herbicide: 'Herbicyd',
  acaricide: 'Akarycyd',
  bactericide: 'Bakteriocyd',
  biological: 'Biologiczny',
  natural: 'Naturalny',
  other: 'Inny',
};

const SPRAY_TYPE_TONE = {
  fungicide: 'amber',
  insecticide: 'rust',
  herbicide: 'rust',
  acaricide: 'rust',
  bactericide: 'amber',
  biological: 'sage',
  natural: 'sage',
  other: 'dim',
};

function daysBetween(a, b) {
  return Math.round((new Date(b) - new Date(a)) / 86400000);
}

function SprayRow({ spray, onDelete, today }) {
  const inWithdrawal = spray.safe_harvest_date && spray.safe_harvest_date >= today;
  const daysToSafe = inWithdrawal ? daysBetween(today, spray.safe_harvest_date) : null;
  const tone = SPRAY_TYPE_TONE[spray.spray_type] || 'dim';
  const label = SPRAY_TYPE_LABEL[spray.spray_type] || spray.spray_type;

  return (
    <div style={{
      padding: '16px 18px', borderRadius: 12,
      background: 'var(--t-bg-elev)',
      border: '1px solid var(--t-line)',
      marginBottom: 10,
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        gap: 14, marginBottom: 10,
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
            marginBottom: 6,
          }}>
            <Display size={16} style={{ display: 'block', letterSpacing: '-.01em' }}>
              {spray.spray_name}
            </Display>
            <Chip tone={tone}>{label}</Chip>
            {inWithdrawal && (
              <Chip tone="rust">⚠ Karencja {daysToSafe}d</Chip>
            )}
          </div>
          <div style={{ fontSize: 12, color: 'var(--t-text-dim)' }}>
            {spray.plot_name} · Rząd {spray.row_number} · {spray.plant_name}
          </div>
        </div>
        <button
          type="button"
          onClick={() => onDelete(spray.id)}
          style={{
            background: 'transparent', border: 'none',
            color: 'var(--t-text-mute)', cursor: 'pointer',
            padding: 4, fontSize: 18, lineHeight: 1,
          }}
          aria-label="Usuń oprysk"
          title="Usuń"
        >×</button>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: 10,
        fontSize: 12, color: 'var(--t-text-dim)',
        paddingTop: 10,
        borderTop: '1px solid var(--t-line)',
      }}>
        <div>
          <Meta style={{ marginBottom: 2 }}>Data</Meta>
          <span style={{ color: 'var(--t-text)' }}>{formatDate(spray.spray_date)}</span>
        </div>
        {spray.dosage && (
          <div>
            <Meta style={{ marginBottom: 2 }}>Dawka</Meta>
            <span style={{ color: 'var(--t-text)' }}>{spray.dosage}</span>
          </div>
        )}
        {spray.withdrawal_period > 0 && (
          <div>
            <Meta style={{ marginBottom: 2 }}>Karencja</Meta>
            <span style={{ color: 'var(--t-text)' }}>{spray.withdrawal_period} dni</span>
          </div>
        )}
        {spray.safe_harvest_date && (
          <div>
            <Meta style={{ marginBottom: 2 }}>Bezpieczny zbiór</Meta>
            <span style={{ color: inWithdrawal ? 'var(--t-rust)' : 'var(--t-sage)' }}>
              {formatDate(spray.safe_harvest_date)}
            </span>
          </div>
        )}
      </div>
      {spray.note && (
        <div style={{
          marginTop: 10, padding: '8px 12px',
          fontSize: 13, color: 'var(--t-text-dim)',
          background: 'var(--t-bg)', borderRadius: 8,
          fontStyle: 'italic', lineHeight: 1.45,
        }}>{spray.note}</div>
      )}
    </div>
  );
}

export default function CareSprays() {
  const [filter, setFilter] = useState('all'); // 'all' | 'active'
  const { data: history, loading, error, refetch } = useApi('/api/sprays/history');

  if (error?.status === 401) {
    return <CareShell><ErrorState error={error} /></CareShell>;
  }
  if (loading) return <CareShell><LoadingState /></CareShell>;

  const today = new Date().toISOString().split('T')[0];
  const all = history || [];
  const active = all.filter(s => s.safe_harvest_date && s.safe_harvest_date >= today);
  const items = filter === 'active' ? active : all;

  const handleDelete = async (id) => {
    if (!await confirmDialog('Usunąć ten oprysk z historii?')) return;
    try {
      await axios.delete(`/api/sprays/${id}`);
      refetch();
    } catch (err) {
      notifyError('Nie udało się usunąć: ' + (err.response?.data?.error || 'błąd serwera'));
    }
  };

  return (
    <CareShell
      eyebrow="Opryski"
      title="Historia zabiegów ochronnych"
      subtitle="Wszystkie opryski Twojego ogrodu, w kolejności od najnowszych. Aktywne w okresie karencji są wyróżnione."
    >
      {/* Statystyki + filtr + add */}
      <div style={{
        display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap',
        marginBottom: 24,
      }}>
        <div style={{
          display: 'flex', gap: 8, padding: 4,
          background: 'var(--t-bg-elev)', borderRadius: 10,
          border: '1px solid var(--t-line)',
        }}>
          {[
            { k: 'all', label: `Wszystkie (${all.length})` },
            { k: 'active', label: `W karencji (${active.length})` },
          ].map(opt => (
            <button
              key={opt.k}
              type="button"
              onClick={() => setFilter(opt.k)}
              style={{
                padding: '8px 14px', borderRadius: 8,
                background: filter === opt.k ? 'var(--t-bg)' : 'transparent',
                border: 'none', cursor: 'pointer',
                color: filter === opt.k ? 'var(--t-text)' : 'var(--t-text-dim)',
                fontFamily: 'var(--t-sans)', fontSize: 13,
                fontWeight: filter === opt.k ? 500 : 400,
              }}
            >{opt.label}</button>
          ))}
        </div>
        <div style={{ flex: 1 }} />
        <Link to="/care/add?type=spray" style={{ textDecoration: 'none' }}>
          <Button variant="primary">+ Nowy oprysk</Button>
        </Link>
      </div>

      {/* Karencja warning */}
      {filter === 'all' && active.length > 0 && (
        <div style={{
          padding: '14px 16px', borderRadius: 10,
          background: 'rgba(192, 106, 74, 0.08)',
          borderLeft: '3px solid var(--t-rust)',
          marginBottom: 24, fontSize: 13, color: 'var(--t-text)',
        }}>
          ⚠ <strong>{active.length}</strong> {active.length === 1 ? 'oprysk jest' : 'oprysków jest'} w trakcie karencji — nie zbieraj plonów do końca okresu.
        </div>
      )}

      <CareSection title={filter === 'active' ? 'W karencji' : 'Wszystkie opryski'}>
        {items.length > 0 ? (
          <div>
            {items.map(spray => (
              <SprayRow
                key={spray.id}
                spray={spray}
                onDelete={handleDelete}
                today={today}
              />
            ))}
          </div>
        ) : (
          <EmptyState
            title={filter === 'active' ? 'Brak aktywnych karencji' : 'Brak zabiegów'}
            description={filter === 'active'
              ? 'Wszystkie zbiory bezpieczne — żaden oprysk nie blokuje plonów.'
              : 'Dodaj pierwszy oprysk, żeby śledzić daty karencji i bezpieczne zbiory.'}
            action={
              <Link to="/care/add?type=spray" style={{ textDecoration: 'none' }}>
                <Button variant="primary">+ Dodaj oprysk</Button>
              </Link>
            }
          />
        )}
      </CareSection>
    </CareShell>
  );
}
